"use client";

import { useCallback, useEffect, useState } from "react";
import { useWorkspaceActivityAutoRefresh } from "@/hooks/useWorkspaceActivityAutoRefresh";
import {
  fetchRecentWorkspaceTransactions,
  RECENT_WORKSPACE_TRANSACTION_LIMIT,
  type RecentWorkspaceTransaction,
} from "@/lib/transactionSummary";

type RefetchOptions = {
  silent?: boolean;
};

export function useWorkspaceRecentTransactions(
  workspaceId?: string | null,
  limit: number = RECENT_WORKSPACE_TRANSACTION_LIMIT,
) {
  const [recentTransactions, setRecentTransactions] = useState<
    RecentWorkspaceTransaction[]
  >([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(
    async (options?: RefetchOptions) => {
      const ws = workspaceId?.trim() ?? "";
      if (!ws) {
        setRecentTransactions([]);
        setError(null);
        setIsLoading(false);
        return;
      }

      const silent = options?.silent ?? false;

      try {
        if (!silent) setIsLoading(true);
        setError(null);
        const records = await fetchRecentWorkspaceTransactions(ws, limit);
        setRecentTransactions(records);
      } catch (err) {
        const message = err instanceof Error ? err.message : "unknown error";
        setError(message);
      } finally {
        if (!silent) setIsLoading(false);
      }
    },
    [limit, workspaceId],
  );

  useEffect(() => {
    void refetch();
  }, [refetch]);

  useWorkspaceActivityAutoRefresh(workspaceId, () =>
    refetch({ silent: true }),
  );

  return {
    recentTransactions,
    isLoading,
    error,
    refetch,
  };
}
